/**
 * ChangeLockIndicator — Shows lock/unlock status for customer addresses.
 * Addresses cannot be reassigned within 90 days of last change.
 * Branch managers can override with confirmation.
 */

import React, { useState, useEffect } from 'react';

const LOCK_DAYS = 90;

export function isLocked(lastChangeDate) {
  if (!lastChangeDate) return false;
  const changed = new Date(lastChangeDate);
  if (isNaN(changed.getTime())) return false;
  const now = new Date();
  const diffMs = now - changed;
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  return diffDays < LOCK_DAYS;
}

export function daysUntilUnlock(lastChangeDate) {
  if (!lastChangeDate) return 0;
  const changed = new Date(lastChangeDate);
  if (isNaN(changed.getTime())) return 0;
  const unlockDate = new Date(changed.getTime() + LOCK_DAYS * 24 * 60 * 60 * 1000);
  const now = new Date();
  const diff = Math.ceil((unlockDate - now) / (1000 * 60 * 60 * 24));
  return Math.max(0, diff);
}

export default function ChangeLockIndicator({ lastChangeDate, onOverride }) {
  const [showConfirm, setShowConfirm] = useState(false);
  const locked = isLocked(lastChangeDate);
  const remaining = daysUntilUnlock(lastChangeDate);

  useEffect(() => {
    if (!showConfirm) return;
    const handler = (e) => { if (e.key === 'Escape') setShowConfirm(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [showConfirm]);

  if (!locked) {
    return (
      <span
        title="Unlocked — can be reassigned"
        className="ro-lock-icon ro-lock-icon-unlocked"
      >
        {'\u{1F513}'}
      </span>
    );
  }

  return (
    <>
      <span
        title={`Locked — ${remaining} day(s) remaining. Click to override.`}
        className={`ro-lock-icon ro-lock-icon-locked${onOverride ? ' ro-lock-icon-clickable' : ''}`}
        onClick={() => onOverride && setShowConfirm(true)}
        role={onOverride ? 'button' : undefined}
        tabIndex={onOverride ? 0 : undefined}
        onKeyDown={onOverride ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowConfirm(true); } } : undefined}
      >
        {'\u{1F512}'}
      </span>

      {showConfirm && (
        <div
          className="ro-modal-backdrop"
          onClick={() => setShowConfirm(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Override change lock confirmation"
        >
          <div
            className="ro-modal-content"
            onClick={e => e.stopPropagation()}
          >
            <h4 className="ro-modal-title">Override Change Lock?</h4>
            <p className="ro-modal-text">
              This address was last changed <strong>{lastChangeDate ? new Date(lastChangeDate).toLocaleDateString() : 'recently'}</strong>.
            </p>
            <p className="ro-modal-text">
              The {LOCK_DAYS}-day lock has <strong>{remaining} day(s)</strong> remaining.
              Overriding allows reassignment before the lock period expires.
            </p>
            <div className="ro-button-group-end">
              <button
                type="button"
                className="ro-btn"
                onClick={() => setShowConfirm(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ro-btn ro-btn-danger"
                onClick={() => {
                  setShowConfirm(false);
                  onOverride();
                }}
              >
                Override Lock
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
